import { useEffect, useState, useMemo } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { OutlineProposalArea } from "@/components/outline-proposal/OutlineProposalArea"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Plus,
  Loader2,
  AlertCircle,
  MoreHorizontal,
  Upload,
  Download,
  Unlock,
  FileQuestion,
} from "lucide-react"
import { useResponsesStore } from "@/stores/responses-store"
import { useDraftingStore } from "@/stores/drafting-store"
import { useSectionAssignmentsStore } from "@/stores/section-assignments-store"
import { useAuthStore } from "@/stores/auth-store"
import { exportArtifactToDocx, deleteQuestion, api, getArtifactSubmissionLinks } from "@/lib/api"
import type { ArtifactSubmissionLink } from "@/lib/api"
import type { QuestionnaireItem } from "@/types/questionnaire-types"
import { collaborationStatusConfig, bucketForStatus, type CollaborationStatusKey } from "@/lib/section-status"
import { type BidMemberInfo, ArtifactOwnerSelector, SectionOwnerCell } from "@/components/ui/owner-assignment"
import { ArtifactPageHeader } from "@/components/ArtifactPageHeader"
import { ArtifactStatusBadge } from "@/components/ArtifactStatusBadge"
import { SubmitForReviewButton } from "@/components/SubmitForReviewButton"
import { ReviewStatusBanner } from "@/components/ReviewStatusBanner"
import { toast } from "sonner"
import { toastExportResult } from '@/lib/pending-ai-change-notices'
import { AddQuestionDialog } from "@/components/questionnaire/AddQuestionDialog"
import { PasteParseDialog } from "@/components/questionnaire/PasteParseDialog"
import { useBidReadOnly, useIsViewOnly } from "@/hooks/useViewOnly"
import { ViewOnlyNotice } from "@/components/ViewOnlyNotice"
import { SubmissionLinksIndicator } from "@/components/extraction/SubmissionLinksIndicator"
import { BriefButton } from "@/components/drafting/BriefButton"
import { BriefModal } from "@/components/drafting/BriefModal"
import { ExportGapConfirmDialog } from "@/components/drafting/ExportGapConfirmDialog"
import { useExportGapCheck } from "@/hooks/useExportGapCheck"
import { useCoverageStore } from "@/stores/coverage-store"

// ===================
// Helper Functions
// ===================

interface QuestionGroup {
  title: string | null
  questions: QuestionnaireItem[]
}

function groupQuestions(questions: QuestionnaireItem[]): QuestionGroup[] {
  const groups: QuestionGroup[] = []
  let currentGroup: string | null | undefined

  for (const q of questions) {
    const group = q.groupTitle ?? null
    if (group !== currentGroup || groups.length === 0) {
      groups.push({ title: group, questions: [] })
      currentGroup = group
    }
    groups[groups.length - 1]!.questions.push(q)
  }
  return groups
}

// ===================
// Progress Summary
// ===================

function ProgressSummary({
  questions,
  assignments,
}: {
  questions: QuestionnaireItem[]
  assignments: Array<{ sectionId: string; collaborationStatus: string }>
}) {
  const total = questions.length
  const getStatus = (key: string) =>
    assignments.find((a) => a.sectionId === key)?.collaborationStatus ?? 'not_started'

  const counts = questions.reduce(
    (acc, q) => {
      acc[bucketForStatus(getStatus(q.sectionKey))] += 1
      return acc
    },
    { approved: 0, awaitingApproval: 0, toWrite: 0 },
  )

  // Answered, not approved — same definition as the outline rollup.
  const answeredPercent = total > 0 ? Math.round(((total - counts.toWrite) / total) * 100) : 0

  return (
    <div className="px-3 py-2 mb-3 flex items-center justify-between">
      <div className="flex items-center gap-3 text-meta">
        <span>{total} question{total !== 1 ? "s" : ""}</span>
        <span>·</span>
        <span>{counts.approved} approved</span>
        <span>·</span>
        <span>{counts.awaitingApproval} awaiting approval</span>
        <span>·</span>
        <span>{counts.toWrite} still to answer</span>
      </div>
      <span className="text-xs font-medium">{answeredPercent}% answered</span>
    </div>
  )
}

// ===================
// Question Row
// ===================

function QuestionRow({
  question,
  statusKey,
  onNavigate,
  onEdit,
  onDelete,
  isFrozen,
  submissionLinks,
  ownerCell,
  briefButton,
}: {
  question: QuestionnaireItem
  statusKey: CollaborationStatusKey
  onNavigate: () => void
  onEdit: () => void
  onDelete: () => void
  isFrozen: boolean
  submissionLinks?: React.ReactNode
  ownerCell?: React.ReactNode
  briefButton?: React.ReactNode
}) {
  const statusInfo = collaborationStatusConfig[statusKey]

  return (
    <tr
      className="group cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={onNavigate}
    >
      {/* Number */}
      <td className="px-3 py-2.5 text-xs font-mono text-muted-foreground">
        {question.number}
      </td>

      {/* Question text */}
      <td className="px-3 py-2.5">
        <p className="text-sm truncate max-w-md">{question.prompt}</p>
      </td>

      {/* Type */}
      <td className="px-3 py-2.5">
        <Badge variant="default" className="text-[10px]">
          Rich text
        </Badge>
      </td>

      {/* Limit */}
      <td className="px-3 py-2.5 text-meta">
        {question.wordLimit ? `${question.wordLimit}` : "—"}
      </td>

      {/* Status */}
      <td className="px-3 py-2.5">
        <Badge variant={statusInfo.variant} dot className="text-xs whitespace-nowrap">
          {statusInfo.label}
        </Badge>
      </td>

      {/* Owner */}
      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
        {ownerCell}
      </td>

      {/* Weight */}
      <td className="px-3 py-2.5 text-meta">
        {question.weight ? `${question.weight}%` : "—"}
      </td>

      {/* Submission Links */}
      <td className="px-3 py-2.5">
        {submissionLinks}
      </td>

      {/* Actions */}
      <td className="px-3 py-2.5">
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          {briefButton}
          {!isFrozen && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit()
                }}
              >
                Edit question
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-muted-foreground focus:text-destructive focus:bg-destructive/10"
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete()
                }}
              >
                Delete question
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
          )}
        </div>
      </td>
    </tr>
  )
}

// ===================
// Main Component
// ===================

export function QuestionnaireOverviewPage() {
  const { bidId, artifactId } = useParams<{ bidId: string; artifactId: string }>()
  const navigate = useNavigate()

  // Store state
  const {
    selectedArtifact: artifact,
    isLoading: artifactLoading,
    error: artifactError,
    fetchArtifactById,
    clearError: clearArtifactError,
    reopenArtifact,
    updateArtifact,
  } = useResponsesStore()

  const {
    outline,
    isLoadingOutline,
    error: outlineError,
    fetchOutline,
    clearError: clearOutlineError,
  } = useDraftingStore()

  const { assignments, fetchAssignments, assignSection, unassignSection } = useSectionAssignmentsStore()
  const currentUserId = useAuthStore((s) => s.user?.id)

  // Local state
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false)
  const [editQuestion, setEditQuestion] = useState<QuestionnaireItem | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<QuestionnaireItem | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [submissionLinks, setSubmissionLinks] = useState<ArtifactSubmissionLink[]>([])

  // Brief modal — opened from a question row, before entering the editor.
  const [briefSection, setBriefSection] = useState<{ sectionKey: string; title: string } | null>(null)
  const [briefOpen, setBriefOpen] = useState(false)
  const loadCoverage = useCoverageStore((s) => s.load)

  // Bid members for owner check + assignment
  const [bidMembers, setBidMembers] = useState<BidMemberInfo[]>([])
  const isBidOwner = bidMembers.some((m) => m.userId === currentUserId && m.role === 'owner')
  const isArtifactOwner = artifact?.ownerId === currentUserId
  // A read-only user — an org viewer, or a member who is not on the bid —
  // reads the questionnaire; every change is refused them.
  const { isReadOnly } = useBidReadOnly(bidId)
  // Export is refused an org viewer only: a member who is not on the bid may
  // still export what they can read.
  const isViewOnly = useIsViewOnly()
  const canAssignQuestions = !isReadOnly && (isBidOwner || isArtifactOwner)
  const isOwner = isBidOwner && !isReadOnly

  // Fetch data on mount
  useEffect(() => {
    if (bidId) void loadCoverage(bidId)
    if (artifactId) {
      void fetchArtifactById(artifactId)
      void fetchOutline(artifactId)
      void fetchAssignments(artifactId)
      void getArtifactSubmissionLinks(artifactId)
        .then((data) => setSubmissionLinks(data.links))
        .catch(() => setSubmissionLinks([]))
    }
  }, [artifactId, bidId, loadCoverage, fetchArtifactById, fetchOutline, fetchAssignments])

  useEffect(() => {
    if (bidId) {
      void api.get<{ members: BidMemberInfo[] }>(`/bids/${bidId}/members`)
        .then((data) => setBidMembers(data.members))
        .catch(() => { /* non-critical */ })
    }
  }, [bidId])

  // Derive questions from outline
  const questions = useMemo(() => {
    return (outline?.sections ?? []) as unknown as QuestionnaireItem[]
  }, [outline])

  const groupedQuestions = useMemo(() => groupQuestions(questions), [questions])

  // Why Submit-for-review is blocked, or null when it's allowed. Same shape as
  // the outline's gate in ArtifactDetailPage, counting questions not sections.
  const submitDisabledReason = useMemo(() => {
    if (questions.length === 0) return 'Add questions before submitting'
    const outstanding = questions.filter((q) => {
      const assignment = assignments.find((a) => a.sectionId === q.sectionKey)
      const status = assignment?.collaborationStatus ?? 'not_started'
      return status !== 'approved' && status !== 'complete'
    }).length
    if (outstanding === 0) return null
    const noun = questions.length === 1 ? 'question' : 'questions'
    const verb = outstanding === 1 ? 'needs' : 'need'
    return `${outstanding} of ${questions.length} ${noun} still ${verb} approval`
  }, [questions, assignments])

  const getStatus = (sectionKey: string): CollaborationStatusKey => {
    const assignment = assignments.find((a) => a.sectionId === sectionKey)
    return (assignment?.collaborationStatus as CollaborationStatusKey) ?? 'not_started'
  }

  // Handlers
  const refreshData = () => {
    if (artifactId) {
      void fetchOutline(artifactId)
      void fetchAssignments(artifactId)
    }
  }

  const handleNavigateToQuestion = (sectionKey: string) => {
    void navigate(`/app/bids/${bidId}/responses/${artifactId}/questionnaire/${sectionKey}`)
  }

  const handleDelete = async () => {
    if (!deleteTarget || !artifactId) return
    try {
      setIsDeleting(true)
      await deleteQuestion(artifactId, deleteTarget.sectionKey)
      toast.success("Question deleted")
      refreshData()
      setDeleteTarget(null)
    } catch {
      toast.error("Failed to delete question")
    } finally {
      setIsDeleting(false)
    }
  }

  const runExport = async () => {
    if (!artifactId) return
    try {
      setIsExporting(true)
      const { pendingAiChanges } = await exportArtifactToDocx(artifactId)
      toastExportResult("Export downloaded", pendingAiChanges)
    } catch {
      toast.error("Export failed")
    } finally {
      setIsExporting(false)
    }
  }

  // A questionnaire's outline entries carry `number`/`prompt`, not `title` —
  // same label the brief modal uses, so the dialog names a question the way the
  // rest of the page does.
  const gapOutlineSections = useMemo(
    () => questions.map((q) => ({ sectionKey: q.sectionKey, title: `${q.number} ${q.prompt}`.trim() })),
    [questions],
  )

  const { runWithGapCheck, exportGapDialogProps } = useExportGapCheck({
    artifactId,
    outlineSections: gapOutlineSections,
    fallbackTitle: artifact?.name ?? "This questionnaire",
    onReviewGaps: (sectionKey) => {
      if (sectionKey) handleNavigateToQuestion(sectionKey)
    },
  })

  // Spec 19: hold the export while answers still show evidence gaps, so the
  // strip is never silent. `runWithGapCheck` runs the export itself when the
  // draft is clean, or when the report call fails.
  const handleExport = async () => {
    if (!artifactId) return
    try {
      setIsExporting(true)
      await runWithGapCheck(runExport)
    } finally {
      setIsExporting(false)
    }
  }

  const handleSetArtifactOwner = async (userId: string | null) => {
    if (!artifact?.id) return
    try {
      await updateArtifact(artifact.id, { ownerId: userId })
      toast.success(userId ? "Questionnaire owner updated" : "Questionnaire owner removed")
    } catch {
      toast.error("Failed to update questionnaire owner")
    }
  }

  const handleReopen = async () => {
    if (!artifactId) return
    try {
      await reopenArtifact(artifactId)
      toast.success("Questionnaire reopened")
    } catch {
      toast.error("Failed to reopen")
    }
  }

  // Loading state
  if ((artifactLoading || isLoadingOutline) && !artifact) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Error state
  if (artifactError || outlineError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">{artifactError || outlineError}</p>
        <Button
          variant="outline"
          onClick={() => {
            clearArtifactError()
            clearOutlineError()
            if (artifactId) {
              void fetchArtifactById(artifactId)
              void fetchOutline(artifactId)
            }
          }}
        >
          Try Again
        </Button>
      </div>
    )
  }

  const isFinalized = artifact?.status === 'finalized'
  const isInReview = artifact?.status === 'pending_review'
  const isFrozen = isFinalized || isInReview
  // The questions can't change: frozen for everyone, or a read-only reader.
  const isLocked = isFrozen || isReadOnly

  return (
    <div className="flex flex-col h-full">
      <ArtifactPageHeader
        title={artifact?.name ?? 'Questionnaire'}
        description={artifact?.description ?? null}
        onBack={() => void navigate(`/app/bids/${bidId}/write`)}
        owner={
          artifact ? (
            <ArtifactOwnerSelector
              artifact={artifact}
              bidMembers={bidMembers}
              canSetOwner={isOwner}
              onSetOwner={(userId) => void handleSetArtifactOwner(userId)}
              compact
            />
          ) : undefined
        }
        titleBadges={
          <>
            <Badge variant="secondary" className="text-[10px]">Questionnaire</Badge>
            <ArtifactStatusBadge status={artifact?.status} />
          </>
        }
        actions={
          <>
            {artifactId && !isLocked && (
              <SubmitForReviewButton
                artifactId={artifactId}
                bidMembers={bidMembers}
                currentUserId={currentUserId}
                canSubmit={isBidOwner}
                disabledReason={submitDisabledReason}
              />
            )}
            {isOwner && isFinalized && (
              <Button variant="ghost" size="sm" onClick={() => void handleReopen()}>
                <Unlock className="h-4 w-4 mr-1.5" />
                Reopen
              </Button>
            )}
            {!isLocked && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="h-9 w-9" aria-label="More actions">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setPasteDialogOpen(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    Paste & Parse
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {/* Export is a POST, which the backend refuses a view-only user. */}
            {!isViewOnly && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void handleExport()}
              disabled={isExporting || questions.length === 0}
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              ) : (
                <Download className="h-4 w-4 mr-1.5" />
              )}
              Export
            </Button>
            )}
            {!isLocked && (
              <Button size="sm" onClick={() => setAddDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-1.5" />
                Add Question
              </Button>
            )}
          </>
        }
      />

      {/* Review banner (pending / changes_requested / finalized) */}
      {artifact && (
        <div className="px-6 pt-3">
          <ReviewStatusBanner
            artifact={artifact}
            currentUserId={currentUserId}
            isBidOwner={isBidOwner}
          />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {/* ⚠ `!isLoadingOutline` is load-bearing: a cached artifact renders
            before its outline lands, so an empty `questions` briefly means
            "loading", not "none" — and the empty state would invite the user to
            extract questions the document already has. */}
        {questions.length === 0 && !isLoadingOutline ? (
          /* Empty state — AI question extraction owns this area (manual paths
             beside it). Frozen questionnaires get the message without the CTA:
             the backend rejects generation and accept on a frozen artifact
             (spec §2-C1), so the button would only produce a raw 400.
             There is deliberately NO additions mount here — question
             re-extraction stays blocked backend-side (spec §3.2.2). */
          isFrozen ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <FileQuestion className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="text-base font-medium mb-1">No questions yet</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                This document is {isFinalized ? 'finalised' : 'in review'}, so its questions can’t
                be changed.
              </p>
            </div>
          ) : isReadOnly ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <FileQuestion className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="text-base font-medium mb-1">No questions yet</h3>
              <ViewOnlyNotice />
            </div>
          ) : (
            <OutlineProposalArea
              bidId={bidId ?? ''}
              artifactId={artifactId ?? ''}
              artifactType="questionnaire"
              onAccepted={() => {
                if (artifactId) void fetchOutline(artifactId)
              }}
              manualActions={
                <>
                  <Button variant="ghost" onClick={() => setPasteDialogOpen(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    Paste & Parse
                  </Button>
                  <Button variant="ghost" onClick={() => setAddDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Question
                  </Button>
                </>
              }
            />
          )
        ) : (
          <>
            {/* Progress summary */}
            <ProgressSummary questions={questions} assignments={assignments} />

            {/* Question table */}
            <div className="border rounded-lg overflow-hidden bg-card shadow-sm">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="w-16 px-3 py-3 text-left text-sm font-medium text-muted-foreground">#</th>
                    <th className="px-3 py-3 text-left text-sm font-medium text-muted-foreground">Question</th>
                    <th className="w-24 px-3 py-3 text-left text-sm font-medium text-muted-foreground">Type</th>
                    <th className="w-16 px-3 py-3 text-left text-sm font-medium text-muted-foreground">Limit</th>
                    <th className="w-32 px-3 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                    <th className="w-10 px-3 py-3 text-left text-sm font-medium text-muted-foreground">Owner</th>
                    <th className="w-20 px-3 py-3 text-left text-sm font-medium text-muted-foreground">Weight</th>
                    <th className="w-48 px-3 py-3 text-left text-sm font-medium text-muted-foreground">Submission</th>
                    <th className="w-12 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {groupedQuestions.map((group, gi) => (
                    <GroupRows
                      key={group.title ?? `__ungrouped_${gi}`}
                      group={group}
                      getStatus={getStatus}
                      onNavigate={handleNavigateToQuestion}
                      onEdit={(q) => setEditQuestion(q)}
                      onDelete={(q) => setDeleteTarget(q)}
                      bidId={bidId ?? ''}
                      artifactId={artifactId ?? ''}
                      submissionLinks={submissionLinks}
                      onLinked={async () => {
                        if (!artifactId) return
                        const data = await getArtifactSubmissionLinks(artifactId)
                        setSubmissionLinks(data.links)
                      }}
                      bidMembers={bidMembers}
                      assignments={assignments}
                      canAssignQuestions={canAssignQuestions}
                      currentUserId={currentUserId}
                      isFrozen={isLocked}
                      onAssignQuestion={(sectionKey, userId) => {
                        if (!artifactId) return
                        const isSelf = userId === currentUserId
                        const member = bidMembers.find((m) => m.userId === userId)
                        const name = member?.fullName || member?.email || "User"
                        void assignSection(artifactId, sectionKey, userId)
                        toast(isSelf ? "Question assigned to you" : `Question assigned to ${name}`, {
                          action: {
                            label: "Undo",
                            onClick: () => void unassignSection(artifactId, sectionKey),
                          },
                        })
                      }}
                      onUnassignQuestion={(sectionKey) => {
                        if (!artifactId) return
                        void unassignSection(artifactId, sectionKey)
                        toast("Question unassigned")
                      }}
                      onOpenBrief={(sectionKey, title) => {
                        setBriefSection({ sectionKey, title })
                        setBriefOpen(true)
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Add Question Dialog */}
      <AddQuestionDialog
        open={addDialogOpen || !!editQuestion}
        onOpenChange={(open) => {
          if (!open) {
            setAddDialogOpen(false)
            setEditQuestion(null)
          } else {
            setAddDialogOpen(true)
          }
        }}
        artifactId={artifactId ?? ''}
        onSuccess={refreshData}
        editQuestion={editQuestion}
      />

      {/* Paste & Parse Dialog */}
      <PasteParseDialog
        open={pasteDialogOpen}
        onOpenChange={setPasteDialogOpen}
        artifactId={artifactId ?? ''}
        onSuccess={refreshData}
      />

      {briefSection && (
        <BriefModal
          open={briefOpen}
          onOpenChange={setBriefOpen}
          bidId={bidId ?? ''}
          artifactId={artifactId ?? ''}
          sectionKey={briefSection.sectionKey}
          unitTitle={briefSection.title}
          scopeKind="unit"
          canWrite={!isLocked}
          onOpenEditor={() => {
            setBriefOpen(false)
            void navigate(`/app/bids/${bidId}/responses/${artifactId}/questionnaire/${briefSection.sectionKey}`)
          }}
        />
      )}

      <ExportGapConfirmDialog {...exportGapDialogProps} />

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Question?</AlertDialogTitle>
            <AlertDialogDescription>
              Question {deleteTarget?.number} and its draft answer will be removed.
              Drafts are kept for 30 days.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDelete()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// ===================
// Group Rows Helper
// ===================

function GroupRows({
  group,
  getStatus,
  onNavigate,
  onEdit,
  onDelete,
  bidId,
  artifactId,
  submissionLinks,
  onLinked,
  bidMembers,
  assignments,
  canAssignQuestions,
  currentUserId,
  isFrozen,
  onAssignQuestion,
  onUnassignQuestion,
  onOpenBrief,
}: {
  group: QuestionGroup
  getStatus: (sectionKey: string) => CollaborationStatusKey
  onNavigate: (sectionKey: string) => void
  onEdit: (q: QuestionnaireItem) => void
  onDelete: (q: QuestionnaireItem) => void
  onOpenBrief: (sectionKey: string, title: string) => void
  bidId: string
  artifactId: string
  submissionLinks: ArtifactSubmissionLink[]
  onLinked: () => void | Promise<void>
  bidMembers: BidMemberInfo[]
  assignments: Array<{ sectionId: string; userId: string | null; fullName: string | null; avatarUrl: string | null }>
  canAssignQuestions: boolean
  currentUserId: string | undefined
  isFrozen: boolean
  onAssignQuestion: (sectionKey: string, userId: string) => void
  onUnassignQuestion: (sectionKey: string) => void
}) {
  return (
    <>
      {group.title && (
        <tr className="bg-muted/20">
          <td
            colSpan={9}
            className="px-3 py-2 text-sm font-medium text-muted-foreground"
          >
            {group.title}
          </td>
        </tr>
      )}
      {group.questions.map((q) => {
        const assignment = assignments.find((a) => a.sectionId === q.sectionKey)
        const assignmentForCell = assignment?.userId
          ? { userId: assignment.userId, fullName: assignment.fullName, avatarUrl: assignment.avatarUrl }
          : undefined

        return (
          <QuestionRow
            key={q.sectionKey}
            question={q}
            statusKey={getStatus(q.sectionKey)}
            onNavigate={() => onNavigate(q.sectionKey)}
            onEdit={() => onEdit(q)}
            onDelete={() => onDelete(q)}
            isFrozen={isFrozen}
            briefButton={
              <BriefButton
                bidId={bidId}
                artifactId={artifactId}
                sectionKey={q.sectionKey}
                showCounts={false}
                onOpen={() => onOpenBrief(q.sectionKey, `${q.number} ${q.prompt}`)}
              />
            }
            submissionLinks={
              <SubmissionLinksIndicator
                bidId={bidId}
                artifactId={artifactId}
                sectionKey={q.sectionKey}
                links={submissionLinks.filter((link) => link.sectionKey === q.sectionKey)}
                onLinked={onLinked}
              />
            }
            ownerCell={
              <SectionOwnerCell
                assignment={assignmentForCell}
                bidMembers={bidMembers}
                canAssign={canAssignQuestions}
                currentUserId={currentUserId}
                onAssign={(userId) => onAssignQuestion(q.sectionKey, userId)}
                onUnassign={() => onUnassignQuestion(q.sectionKey)}
                isFinalized={isFrozen}
              />
            }
          />
        )
      })}
    </>
  )
}
